const ERROR_RELOAD_KEY = 'kt_error_reload_attempt_v1';
const ERROR_RELOAD_TTL_MS = 10 * 60 * 1000;
const RECOVERY_PARAM = 'kt_recovered';
const RECOVERY_KEY_PARAM = 'kt_recovery_key';

declare const __APP_VERSION__: string | undefined;

interface ReloadRecord {
  key: string;
  at: number;
}

const recoverableLoadErrorPatterns = [
  /ChunkLoadError/i,
  /Loading chunk .* failed/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Unable to preload CSS/i,
];

const getErrorText = (error: unknown): string => {
  if (error instanceof Error) {
    return `${error.name} ${error.message} ${error.stack ?? ''}`;
  }

  if (typeof error === 'string') {
    return error;
  }

  if (error && typeof error === 'object') {
    const maybeError = error as { message?: unknown; reason?: unknown; type?: unknown };
    return [maybeError.message, maybeError.reason, maybeError.type].filter(Boolean).join(' ');
  }

  return '';
};

const getReloadKey = (reason: string): string => {
  const version = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'dev';
  return `${version}:${window.location.pathname}:${reason}`;
};

const readReloadRecord = (): ReloadRecord | null => {
  try {
    const raw = sessionStorage.getItem(ERROR_RELOAD_KEY);
    return raw ? (JSON.parse(raw) as ReloadRecord) : null;
  } catch {
    return null;
  }
};

const writeReloadRecord = (record: ReloadRecord): void => {
  try {
    sessionStorage.setItem(ERROR_RELOAD_KEY, JSON.stringify(record));
  } catch {
    // Storage can be unavailable in private modes; reloading is still safe.
  }
};

export const isRecoverableLoadError = (error: unknown): boolean => {
  const errorText = getErrorText(error);
  return recoverableLoadErrorPatterns.some(pattern => pattern.test(errorText));
};

export const reloadOnceForErrorRecovery = (reason: string): boolean => {
  if (typeof window === 'undefined') return false;

  const now = Date.now();
  const key = getReloadKey(reason);
  const previous = readReloadRecord();
  const currentUrl = new URL(window.location.href);
  const recoveredAt = Number(currentUrl.searchParams.get(RECOVERY_PARAM));
  const recoveredKey = currentUrl.searchParams.get(RECOVERY_KEY_PARAM);

  if (
    (previous?.key === key && now - previous.at < ERROR_RELOAD_TTL_MS) ||
    (recoveredKey === key &&
      Number.isFinite(recoveredAt) &&
      now - recoveredAt < ERROR_RELOAD_TTL_MS)
  ) {
    return false;
  }

  writeReloadRecord({ key, at: now });

  void navigator.serviceWorker?.getRegistration?.().then(registration => registration?.update());

  const nextUrl = currentUrl;
  nextUrl.searchParams.set(RECOVERY_PARAM, now.toString());
  nextUrl.searchParams.set(RECOVERY_KEY_PARAM, key);
  window.location.replace(nextUrl.toString());
  return true;
};

export const reloadOnceForRecoverableLoadError = (error: unknown): boolean => {
  if (!isRecoverableLoadError(error)) return false;
  return reloadOnceForErrorRecovery('chunk-load');
};
